import React, { useState, useReducer, useEffect } from "react";
import { ThemeProvider } from "styled-components";
import styled from "styled-components";
import { useUserSettings } from "components/Site/Contexts/UserSettings";
import { themeFromSiteConfig } from "components/Site/Contexts/SiteTheme";
import { useSiteConfig } from "components/Site/Contexts/SiteConfig";

import {
  AppBarItem,
  AppBarSpacer,
  PickerContainer,
  PickerGroup,
  StyledButtonItem,
  SearchButtonItem,
  ParrallelButtonItem
} from "../../UI/Pickers";
import SearchIcon from "../../../icons/SearchIcon";
import CloseIcon from "../../../icons/CloseIcon";
import ParallelIcon from "../../../icons/LayoutThreeColumnsIcon";
import AppBar from "../../AppBar/AppBar";
import BibleDropdown from "./BibleDropdown/BibleDropdown";
import BookChapterDropdown from "./BookChapterDropdown/BookChapterDropdown";
import SearchInput from "../../SearchResults/SearchInput";
import { useScriptureRouting } from "components/Bible/useScriptureRouting";

const MainPickerContainer = styled(PickerContainer)`
  flex: 1 0 20%;
  justify-content: left;
`;

const MainMenuPickerGroup = styled(PickerGroup)`
  z-index: 10;
  display: flex;
  flex-direction: row;
`;

const ParallelPickerGroup = styled(PickerGroup)`
  max-width: 100px;
`;

interface ChapterBarProps {
  setModeDefault: () => void;
}

const ChapterBar = ({ setModeDefault }: ChapterBarProps) => {
  const [searchOpen, setSearchOpen] = useState(false);
  const [openSubMenuIndex, setOpenSubMenuIndex] = useState<number | null>(null);
  const site = useSiteConfig();
  const { darkMode } = useUserSettings();
  const {
    addParallel,
    removeParallel,
    bibleId,
    parallelBibleIds
  } = useScriptureRouting();

  /* start of temporary solution to make parallels button appear/disappear on orientationchage/resize */
  const [, forceUpdate] = useReducer(x => x + 1, 0);

  useEffect(() => {
    const handleResize = () => window.addEventListener("resize", forceUpdate);
    window.addEventListener("orientationchange", handleResize);

    return () => {
      window.removeEventListener("orientationchange", handleResize);
      window.removeEventListener("resize", forceUpdate);
    };
  }, []);
  /* end of temporary solution to make parallels button appear/disappear on orientationchage/resize */

  // Checking two values solves issue on iOS where window.innerWidth returns an invalid value when resizing
  // ref: https://bugs.webkit.org/show_bug.cgi?id=170595
  const windowWidth = Math.max(
    window.innerWidth,
    document.documentElement.clientWidth
  );
  const canHaveParallel =
    (site.bibles.length > 1 || site.includeAllBibles) && windowWidth >= 500;

  const toggleSubmenu = (index: number) => (reset?: boolean) => {
    setModeDefault();
    setOpenSubMenuIndex(index === openSubMenuIndex || reset ? null : index);
  };

  const closeSubmenus = () => setOpenSubMenuIndex(null);
  const toggleSearch = () => {
    setSearchOpen(!searchOpen);
    setOpenSubMenuIndex(null);
  };
  const onParallelAddClick = () => {
    closeSubmenus();
    addParallel();
  };
  const onParallelRemoveClick = (index: number) => {
    closeSubmenus();
    removeParallel(index);
  };

  return (
    <ThemeProvider theme={{ ...themeFromSiteConfig(site), darkMode }}>
      <AppBar showMenuButton>
        <AppBarSpacer />
        <MainPickerContainer key="main-menu">
          <MainMenuPickerGroup key={bibleId}>
            <SearchButtonItem onClick={toggleSearch}>
              <SearchIcon size={22} />
            </SearchButtonItem>

            {searchOpen ? (
              <>
                <SearchInput key="search" />
                <StyledButtonItem onClick={toggleSearch} key="close-search">
                  <CloseIcon size={16} />
                </StyledButtonItem>
              </>
            ) : (
              <>
                <AppBarItem key={`primary-${bibleId}`} id="bible-dropdown">
                  <BibleDropdown
                    isOpen={openSubMenuIndex === 0}
                    toggle={toggleSubmenu(0)}
                  />
                </AppBarItem>
                <AppBarItem>
                  <BookChapterDropdown
                    isOpen={openSubMenuIndex === 1}
                    toggle={toggleSubmenu(1)}
                  />
                </AppBarItem>
                {canHaveParallel && (
                  <ParrallelButtonItem onClick={onParallelAddClick}>
                    <ParallelIcon size={22} />
                  </ParrallelButtonItem>
                )}
              </>
            )}
          </MainMenuPickerGroup>
        </MainPickerContainer>

        {// Parallel bible dropdowns
        !searchOpen &&
          parallelBibleIds.map((parallelId, index) => (
            <MainPickerContainer key={`${parallelId}-${index}`}>
              <ParallelPickerGroup>
                <AppBarItem>
                  <BibleDropdown
                    isOpen={openSubMenuIndex === index + 2}
                    parallelIndex={index}
                    toggle={toggleSubmenu(index + 2)}
                  />
                </AppBarItem>
                <StyledButtonItem onClick={() => onParallelRemoveClick(index)}>
                  <CloseIcon size={16} className="close-parallel" />
                </StyledButtonItem>
              </ParallelPickerGroup>
            </MainPickerContainer>
          ))}

        {// Do we still need this?
        parallelBibleIds.length > 0 && <AppBarSpacer />}
      </AppBar>
    </ThemeProvider>
  );
};

export default ChapterBar;
